import { useState, useImperativeHandle, useEffect, useRef } from "react"

import type { ToastProps } from "../../atoms/toast"

import { Toast } from "../../atoms/toast"
import styles from "./index.module.css"

type ToastWithId = ToastProps & {
  id: string
  duration?: number
  isExiting?: boolean
}

export type ToasterHandle = {
  addToast: (toast: Omit<ToastWithId, "id" | "isExiting">) => string // Return ID
  removeToast: (id: string) => void
  clearAllToasts: () => void
}

type ToasterProps = {
  maxToasts?: number
  defaultDuration?: number
  ref: React.ForwardedRef<ToasterHandle>
}

export const Toaster = ({ ref, maxToasts = 10, defaultDuration = 10000 }: ToasterProps) => {
  const [toasts, setToasts] = useState<ToastWithId[]>([])
  const timeoutRefs = useRef<Map<string, NodeJS.Timeout>>(new Map())

  const removeToast = (id: string) => {
    const timeout = timeoutRefs.current.get(id)
    if (timeout) {
      clearTimeout(timeout)
      timeoutRefs.current.delete(id)
    }

    setToasts((prev) => prev.map((toast) => (toast.id === id ? { ...toast, isExiting: true } : toast)))

    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id))
    }, 300)
  }

  useImperativeHandle(ref, () => ({
    addToast: (toast) => {
      const id = crypto.randomUUID()

      setToasts((prev) => {
        const newToasts = [...prev, { ...toast, id, isExiting: false }]
        return newToasts.slice(-maxToasts)
      })

      const timeout = setTimeout(() => {
        removeToast(id)
      }, toast.duration ?? defaultDuration)

      timeoutRefs.current.set(id, timeout)

      return id // Return generated ID
    },
    removeToast,
    clearAllToasts: () => {
      timeoutRefs.current.forEach((timeout) => clearTimeout(timeout))
      timeoutRefs.current.clear()

      setToasts((prev) => prev.map((toast) => ({ ...toast, isExiting: true })))
      setTimeout(() => setToasts([]), 300)
    },
  }))

  // Cleanup timeouts on unmount
  useEffect(() => {
    // Capture the current Map instance
    const timeouts = timeoutRefs.current

    return () => {
      // Use the captured Map instance in cleanup
      timeouts.forEach((timeout) => clearTimeout(timeout))
      timeouts.clear()
    }
  }, [])

  return (
    <div className={styles.toaster} role="alert" aria-live="polite" aria-relevant="additions removals">
      {toasts.map(({ id, isExiting, ...toastProps }) => (
        <Toast key={id} {...toastProps} isExiting={isExiting} onClose={() => removeToast(id)} />
      ))}
    </div>
  )
}

Toaster.displayName = "Toaster"
